import { useState, useEffect, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { supabase } from "../lib/supabase";
import {
  SPECIALTIES,
  getSpecialtiesByCategory,
  type Specialty,
} from "../data/specialties";

// ============================================
// ICONS
// ============================================

function IconUser({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
      />
    </svg>
  );
}

function IconCheck({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

function IconX({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function IconChevronDown({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

function IconStethoscope({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12a7.5 7.5 0 0015 0m-15 0a7.5 7.5 0 1115 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077l1.41-.513m14.095-5.13l1.41-.513M5.106 17.785l1.15-.964m11.49-9.642l1.149-.964M7.501 19.795l.75-1.3m7.5-12.99l.75-1.3m-6.063 16.658l.26-1.477m2.605-14.772l.26-1.477m0 17.726l-.26-1.477M10.698 4.614l-.26-1.477M16.5 19.794l-.75-1.299M7.5 4.205L12 12m0 0l4.5-7.795"
      />
    </svg>
  );
}

// ============================================
// SPECIALTY MULTI-SELECT
// ============================================

interface SpecialtyMultiSelectProps {
  selectedSpecialties: string[];
  onChange: (specialties: string[]) => void;
}

function SpecialtyMultiSelect({
  selectedSpecialties,
  onChange,
}: SpecialtyMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const groupedSpecialties = getSpecialtiesByCategory();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredGroupedSpecialties = Object.entries(groupedSpecialties).reduce(
    (acc, [category, specs]) => {
      const filtered = specs.filter((s) =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (filtered.length > 0) {
        acc[category] = filtered;
      }
      return acc;
    },
    {} as Record<string, Specialty[]>
  );

  const toggleSpecialty = (specialtyId: string) => {
    if (selectedSpecialties.includes(specialtyId)) {
      onChange(selectedSpecialties.filter((id) => id !== specialtyId));
    } else {
      onChange([...selectedSpecialties, specialtyId]);
    }
  };

  const removeSpecialty = (specialtyId: string) => {
    onChange(selectedSpecialties.filter((id) => id !== specialtyId));
  };

  const selectedSpecialtyObjects = selectedSpecialties
    .map((id) => SPECIALTIES.find((s) => s.id === id))
    .filter(Boolean) as Specialty[];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected specialties as pills + input */}
      <div
        className={`min-h-[46px] px-3 py-2 bg-white border rounded-xl cursor-text transition-all flex flex-wrap gap-2 items-center ${
          isOpen
            ? "border-teal-500 ring-2 ring-teal-500/20"
            : "border-slate-200 hover:border-slate-300"
        }`}
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        {selectedSpecialtyObjects.map((specialty) => (
          <span
            key={specialty.id}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-700 text-sm font-medium rounded-lg"
          >
            {specialty.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeSpecialty(specialty.id);
              }}
              className="p-0.5 hover:bg-teal-100 rounded transition-colors"
            >
              <IconX className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={
            selectedSpecialties.length === 0 ? "Select specialties..." : ""
          }
          className="flex-1 min-w-[120px] outline-none text-sm placeholder:text-slate-400"
        />
        <IconChevronDown
          className={`w-5 h-5 text-slate-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
          {Object.keys(filteredGroupedSpecialties).length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500">
              No specialties found
            </div>
          ) : (
            Object.entries(filteredGroupedSpecialties).map(
              ([category, specs]) => (
                <div key={category}>
                  <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">
                    {category}
                  </div>
                  {specs.map((specialty) => {
                    const isSelected = selectedSpecialties.includes(
                      specialty.id
                    );
                    return (
                      <button
                        key={specialty.id}
                        type="button"
                        onClick={() => toggleSpecialty(specialty.id)}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-slate-50 transition-colors ${
                          isSelected ? "bg-teal-50" : ""
                        }`}
                      >
                        <span
                          className={
                            isSelected
                              ? "text-teal-700 font-medium"
                              : "text-slate-700"
                          }
                        >
                          {specialty.name}
                        </span>
                        {isSelected && (
                          <IconCheck className="w-4 h-4 text-teal-600" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )
            )
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// PROFILE DATA - EXPORTED FOR USE BY OTHER COMPONENTS
// ============================================

export interface PhysicianProfileData {
  physician_name: string;
  credentials: string;
  npi_number: string;
  practice_name: string;
  phone_number: string;
  fax_number: string;
  state: string;
  specialties: string[];
}

const EMPTY_PROFILE: PhysicianProfileData = {
  physician_name: "",
  credentials: "",
  npi_number: "",
  practice_name: "",
  phone_number: "",
  fax_number: "",
  state: "",
  specialties: [],
};

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
];

// ============================================
// HOOK FOR OTHER COMPONENTS
// ============================================

export function usePhysicianProfile() {
  const { user } = useUser();
  const [profile, setProfile] = useState<PhysicianProfileData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProfile() {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("physician_profiles")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error && error.code !== "PGRST116") {
          throw error;
        }

        if (data) {
          setProfile({
            physician_name: data.physician_name || "",
            credentials: data.credentials || "",
            npi_number: data.npi_number || "",
            practice_name: data.practice_name || "",
            phone_number: data.phone_number || "",
            fax_number: data.fax_number || "",
            state: data.state || "",
            specialties: data.specialties || [],
          });
        }
      } catch (err) {
        console.error("Error loading profile:", err);
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [user]);

  return { profile, loading };
}

// ============================================
// MAIN COMPONENT
// ============================================

export function PhysicianProfile() {
  const { user } = useUser();
  const [profile, setProfile] = useState<PhysicianProfileData>(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    async function loadProfile() {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from("physician_profiles")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error && error.code !== "PGRST116") {
          // PGRST116 = no rows found, which is fine for new users
          throw error;
        }

        if (data) {
          setProfile({
            physician_name: data.physician_name || "",
            credentials: data.credentials || "",
            npi_number: data.npi_number || "",
            practice_name: data.practice_name || "",
            phone_number: data.phone_number || "",
            fax_number: data.fax_number || "",
            state: data.state || "",
            specialties: data.specialties || [],
          });
        }
      } catch (err) {
        console.error("Error loading profile:", err);
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [user]);

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { error } = await supabase.from("physician_profiles").upsert(
        {
          user_id: user.id,
          ...profile,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );

      if (error) throw error;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving profile:", err);
      setError("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (
    field: keyof PhysicianProfileData,
    value: string | string[]
  ) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-200 rounded w-1/4"></div>
          <div className="h-10 bg-slate-100 rounded"></div>
          <div className="h-10 bg-slate-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 rounded-lg">
            <IconUser className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-800">Physician Profile</h2>
            <p className="text-sm text-slate-500">
              Your information for appeal letters and P2P calls
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="p-6 space-y-6">
        {/* Error/Success messages */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <IconCheck className="w-4 h-4" />
            Profile saved successfully!
          </div>
        )}

        {/* Specialty Selection - Featured at top */}
        <div className="p-4 bg-gradient-to-r from-teal-50/50 to-emerald-50/50 border border-teal-100 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <IconStethoscope className="w-5 h-5 text-teal-600" />
            <label className="block text-sm font-semibold text-slate-700">
              Your Specialty
            </label>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Select your specialty to see the most relevant quick templates first
            when creating cases.
          </p>
          <SpecialtyMultiSelect
            selectedSpecialties={profile.specialties}
            onChange={(specialties) => updateField("specialties", specialties)}
          />
        </div>

        {/* Name & Credentials Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Full Name
            </label>
            <input
              type="text"
              value={profile.physician_name}
              onChange={(e) => updateField("physician_name", e.target.value)}
              placeholder="Dr. Jane Smith"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Credentials
            </label>
            <input
              type="text"
              value={profile.credentials}
              onChange={(e) => updateField("credentials", e.target.value)}
              placeholder="MD, FACP"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
          </div>
        </div>

        {/* NPI & Practice */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              NPI Number
            </label>
            <input
              type="text"
              value={profile.npi_number}
              onChange={(e) => updateField("npi_number", e.target.value)}
              placeholder="1234567890"
              maxLength={10}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Practice Name
            </label>
            <input
              type="text"
              value={profile.practice_name}
              onChange={(e) => updateField("practice_name", e.target.value)}
              placeholder="City Medical Associates"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
          </div>
        </div>

        {/* Contact Info */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Phone
            </label>
            <input
              type="tel"
              value={profile.phone_number}
              onChange={(e) => updateField("phone_number", e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Fax
            </label>
            <input
              type="tel"
              value={profile.fax_number}
              onChange={(e) => updateField("fax_number", e.target.value)}
              placeholder="(555) 123-4568"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              State
            </label>
            <select
              value={profile.state}
              onChange={(e) => updateField("state", e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 bg-white"
            >
              <option value="">Select state...</option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-4 border-t border-slate-100">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-medium rounded-xl hover:from-teal-600 hover:to-teal-700 shadow-md shadow-teal-500/25 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <svg
                  className="animate-spin w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <IconCheck className="w-4 h-4" />
                Save Profile
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PhysicianProfile;
